import React, { useEffect, useState } from 'react';
import { CalendarIcon, UserGroupIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNotificationStore } from '../store/notificationStore';
import { useXero } from '../contexts/XeroContext';
import { useXeroApi } from '../hooks/useXeroApi';

interface XeroProject {
  projectId: string;
  name: string;
  status: string;
  contact: {
    name: string;
  };
  deadlineUtc: string;
  totalMinutes: number;
}

export function Projects() {
  const { addNotification } = useNotificationStore();
  const { isAuthenticated, authenticate } = useXero();
  const xeroApi = useXeroApi();
  const [xeroProjects, setXeroProjects] = useState<XeroProject[]>([]);
  const [isLoadingXero, setIsLoadingXero] = useState(false);
  const [xeroError, setXeroError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadXeroProjects();
    }
  }, [isAuthenticated]);

  const loadXeroProjects = async () => {
    setIsLoadingXero(true);
    setXeroError(null);
    try {
      const response = await xeroApi.getProjects();
      setXeroProjects(response.projects);
      addNotification({
        title: 'Xero Projects',
        message: `Successfully loaded ${response.projects.length} projects from Xero`,
        type: 'success'
      });
    } catch (error) {
      console.error('Failed to load Xero projects:', error);
      setXeroError('Failed to load projects from Xero');
      addNotification({
        title: 'Xero Error',
        message: 'Failed to load projects from Xero',
        type: 'error'
      });
    } finally {
      setIsLoadingXero(false);
    }
  };

  if (isLoadingXero && !xeroProjects.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-gray-800 rounded-lg p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-400">Loading Xero projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Projects</h1>
            <p className="text-gray-400">Manage your Xero projects</p>
          </div>
          {!isAuthenticated ? (
            <button
              onClick={() => authenticate()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Connect to Xero
            </button>
          ) : (
            <button
              onClick={loadXeroProjects}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center"
              disabled={isLoadingXero}
            >
              <ArrowPathIcon className={`h-5 w-5 mr-2 ${isLoadingXero ? 'animate-spin' : ''}`} />
              Refresh Projects
            </button>
          )}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg overflow-hidden">
        {xeroError ? (
          <div className="p-8 text-center">
            <p className="text-red-500 mb-4">{xeroError}</p>
            <button
              onClick={loadXeroProjects}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : !isAuthenticated ? (
          <div className="p-8 text-center">
            <p className="text-gray-400 mb-4">Connect to Xero to view your projects</p>
            <button
              onClick={() => authenticate()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Connect to Xero
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Project
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Client
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Deadline
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    Hours
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {xeroProjects.map((project) => (
                  <tr key={project.projectId} className="hover:bg-gray-750">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {project.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {project.contact?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                        ${project.status === 'CLOSED' ? 'bg-green-100 text-green-800' : 
                          project.status === 'INPROGRESS' ? 'bg-blue-100 text-blue-800' : 
                          'bg-yellow-100 text-yellow-800'}`}>
                        {project.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {project.deadlineUtc ? new Date(project.deadlineUtc).toLocaleDateString() : 'No deadline'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-4 w-4 mr-2 text-gray-400" />
                        {Math.round(project.totalMinutes / 60)} hours
                      </div>
                    </td>
                  </tr>
                ))}
                {xeroProjects.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                      No projects found in Xero
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}